import React from 'react';

import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/Button';
import Drawer from 'components/Drawer';
import PlaylistImageSelector from 'components/Playlists/PlaylistImageSelector';
import TextInput from 'components/TextInput';

import {
  PlaylistTypeEnum,
  UserPlaylistsDocument,
  useCreatePlaylistMutation,
} from 'graphql/generated/graphql';

import useGraphaelDefaults from 'hooks/useGraphaelDefaults';
import { useTracking } from 'hooks/useTracking';

import {
  closeAddStoryToPlaylist,
  closePlaylistCreation,
  getNumberOfCurrentPlaylists,
  getPlalistImageId,
  getPlaylistname,
  getStoryIdToBeAdded,
  setName,
} from 'store/modules/playlist';

import './style.scss';

interface FormFields {
  playlistName: string;
  playlistImageId: string;
}

const AddPlaylistName = () => {
  const { register, handleSubmit } =
    useForm<Pick<FormFields, 'playlistName'>>();

  const { formatMessage } = useIntl();

  const { trackContentInteraction } = useTracking();

  const dispatch = useDispatch();

  const numberOfCurrentPlaylists = useSelector(getNumberOfCurrentPlaylists);

  const onSubmit = (data: Pick<FormFields, 'playlistName'>) => {
    trackContentInteraction(
      'click',
      'playlists-add-name',
      'playlists-add-name',
    );
    dispatch(setName(data.playlistName));
  };

  return (
    <>
      <div className="CreatePlaylist__PlaylistName-input">
        <TextInput
          type="text"
          label={formatMessage({
            id: 'SM0123.New playlist flow_P1_playlist title placeholder',
          })}
          name="playlistName"
          inputProps={{
            defaultValue: `Playlist ${numberOfCurrentPlaylists + 1}`,
            ...register('playlistName', {
              required: true,
            }),
            'aria-label': 'playlist name',
          }}
        />
      </div>
      <div className="CreatePlaylist__PlaylistName-button">
        <Button
          variant="primary"
          onClick={handleSubmit(onSubmit)}
          title={{
            id: 'SM0123.New playlist flow_P1_CTA',
          }}
        />
      </div>
    </>
  );
};

const CreatePlaylist = () => {
  const graphaelDefaults = useGraphaelDefaults();
  const dispatch = useDispatch();
  const { trackContentInteraction } = useTracking();

  const storyToBeAdded = useSelector(getStoryIdToBeAdded);

  const [createPlaylist] = useCreatePlaylistMutation({
    ...graphaelDefaults,
    onCompleted: () => {
      dispatch(closePlaylistCreation());
      dispatch(closeAddStoryToPlaylist());
    },
    refetchQueries: [
      {
        query: UserPlaylistsDocument,
        context: graphaelDefaults.context,
        variables: {
          types: [PlaylistTypeEnum.User],
        },
      },
    ],
  });

  const playlistName = useSelector(getPlaylistname);
  const playlistimageId = useSelector(getPlalistImageId);

  const onClose = () => dispatch(closePlaylistCreation());

  const storiesIds = storyToBeAdded ? [storyToBeAdded.id] : [];

  return (
    <Drawer
      icon="Cancel"
      anchor="bottom"
      onClose={onClose}
      open={true}
      header={
        playlistName
          ? { id: 'SM0123.New playlist flow_P2_Title' }
          : { id: 'SM0123.New playlist flow_P1_Title' }
      }
    >
      <div className="CreatePlaylist">
        <div className="CreatePlaylist__content">
          {!playlistName && <AddPlaylistName />}
          {playlistName && !playlistimageId && (
            <PlaylistImageSelector
              buttonTranslationId="SM0123.New playlist flow_P2_CTA"
              onClick={(selectedPlaylistImage, position) => {
                trackContentInteraction(
                  'click',
                  'playlists-choose-header',
                  `playlists-choose-header-[${position}]`,
                );

                createPlaylist({
                  variables: {
                    input: {
                      name: playlistName,
                      playlistImageId: selectedPlaylistImage.id,
                      storiesIds,
                    },
                  },
                });
              }}
            />
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default CreatePlaylist;
